/**
 * Composable Report Widget shell.
 * Optional visualization + optional table; single dataset with limit/load-more driving both.
 */
import { Box, Typography } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';

import { ReportWidgetChart } from './ReportWidgetChart';
import { ReportWidgetTable } from './ReportWidgetTable';
import { getChartColors } from './chartColors';

import { getChartItemId } from '../model/types';

import type {
    ReportWidgetConfig,
    ReportWidgetInteractionPayload,
    ReportWidgetItem,
    ReportWidgetTableConfig,
} from '../model/types';

interface ComposableReportWidgetProps {
    config: ReportWidgetConfig;
    items: ReportWidgetItem[];
    /** When provided, chart uses this instead of (limited) items. Use for e.g. pie with first N + "Other". */
    chartItems?: ReportWidgetItem[];
    loading?: boolean;
    disableChartAnimation?: boolean;
}

function buildPayload(
    widgetId: string,
    interactionType: ReportWidgetInteractionPayload['interactionType'],
    item: ReportWidgetItem,
): ReportWidgetInteractionPayload {
    return {
        widgetId,
        interactionType,
        itemId: item.id,
        itemLabel: item.label,
        item,
        meta: item.meta,
    };
}

function titleToTestClass(title: string): string {
    return (
        title
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '') || 'composable-report-widget'
    );
}

export const ComposableReportWidget = memo(
    ({
        config,
        items,
        chartItems: chartItemsOverride,
        loading = false,
        disableChartAnimation = false,
    }: ComposableReportWidgetProps) => {
        const {
            widgetId,
            title,
            description,
            visualization,
            table: tableConfig,
            pagination,
            onRowClick,
            onChartItemClick,
            onDetailsClick,
        } = config;

        const table: ReportWidgetTableConfig = tableConfig ?? {
            enabled: false,
            columns: [],
        };

        const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

        const layout = config.layout ?? 'stacked';
        const isSideBySide = layout === 'sideBySide';

        const isInitialLoading = loading && items.length === 0;
        const isLoadingMore = loading && items.length > 0;

        // Pagination setup
        // Supports no pagination, client-side pagination, and server-side pagination.
        const usesClientPagination = pagination?.mode === 'client';
        const usesServerPagination = pagination?.mode === 'server';

        const [limit, setLimit] = useState(
            usesClientPagination && pagination
                ? pagination.pageSize
                : items.length,
        );

        const visibleItems = useMemo(
            () => (usesClientPagination ? items.slice(0, limit) : items),
            [items, limit, usesClientPagination],
        );

        const chartItems =
            chartItemsOverride ?? (table.enabled ? visibleItems : items);

        const internalHasMore =
            usesClientPagination && items.length > 0 && limit < items.length;

        const externalHasMore =
            usesServerPagination && pagination ? pagination.canLoadMore : false;

        const hasMore = internalHasMore || externalHasMore;
        const loadMoreCount = internalHasMore ? items.length - limit : 0;

        const handleLoadMore = useCallback(() => {
            if (internalHasMore && usesClientPagination && pagination) {
                const revealAll =
                    pagination.mode === 'client' &&
                    pagination.revealAllOnLoadMore === true;
                const step = revealAll ? items.length : pagination.pageSize;
                setLimit((prev: number) => Math.min(prev + step, items.length));
                return;
            }

            if (externalHasMore && usesServerPagination && pagination) {
                pagination.onLoadMore();
            }
        }, [
            internalHasMore,
            externalHasMore,
            usesClientPagination,
            usesServerPagination,
            pagination,
            items.length,
        ]);

        // Chart colors
        const paletteColors = useMemo(
            () =>
                getChartColors({
                    palette: visualization.options?.palette,
                    colors: visualization.options?.colors,
                }),
            [visualization.options?.palette, visualization.options?.colors],
        );
        const itemColors = useMemo(
            () =>
                visibleItems.map(
                    (item, i) =>
                        item.color ?? paletteColors[i % paletteColors.length],
                ),
            [visibleItems, paletteColors],
        );

        // Event handlers
        const handleRowClick = useCallback(
            (item: ReportWidgetItem) => {
                onRowClick?.(buildPayload(widgetId, 'row_click', item));
            },
            [widgetId, onRowClick],
        );

        const handleChartItemClick = useCallback(
            (item: ReportWidgetItem) => {
                onChartItemClick?.(
                    buildPayload(widgetId, 'chart_item_click', item),
                );
            },
            [widgetId, onChartItemClick],
        );

        const handleDetailsClick = useCallback(
            (item: ReportWidgetItem) => {
                onDetailsClick?.(buildPayload(widgetId, 'details_click', item));
            },
            [widgetId, onDetailsClick],
        );

        return (
            <Box
                data-testclass={
                    title
                        ? titleToTestClass(`report-${title}`)
                        : `composable-report-widget--${widgetId}`
                }
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: config.hasTopLegend ? '10px' : '18px',
                    padding: '24px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                    boxSizing: 'border-box',
                }}
            >
                {title && (
                    <Box sx={{ marginBottom: description ? 0.5 : 0 }}>
                        <Typography variant="h6" component="h2">
                            {title}
                        </Typography>
                        {description && (
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', marginTop: 0.5 }}
                            >
                                {description}
                            </Typography>
                        )}
                    </Box>
                )}

                {isSideBySide && visualization.enabled && table.enabled ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '24px',
                            alignItems: 'stretch',
                            minWidth: 0,
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                width: '280px',
                                minWidth: '280px',
                                maxWidth: '280px',
                                flex: '0 0 280px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <ReportWidgetChart
                                items={chartItems}
                                chartType={visualization.type}
                                options={visualization.options}
                                loading={loading}
                                disableAnimation={disableChartAnimation}
                                onItemClick={
                                    onChartItemClick
                                        ? handleChartItemClick
                                        : undefined
                                }
                                activeItemId={hoveredItemId ?? undefined}
                                onItemHover={(item) =>
                                    setHoveredItemId(item?.id ?? null)
                                }
                            />
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                width: 0,
                                minWidth: 0,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                            }}
                        >
                            <ReportWidgetTable
                                items={visibleItems}
                                columns={table.columns}
                                loading={isInitialLoading}
                                loadingMore={isLoadingMore}
                                hasMore={hasMore}
                                onLoadMore={handleLoadMore}
                                itemColors={itemColors}
                                options={{
                                    ...table.options,
                                    loadMoreCount,
                                }}
                                onRowClick={
                                    onRowClick ? handleRowClick : undefined
                                }
                                onDetailsClick={
                                    onDetailsClick
                                        ? handleDetailsClick
                                        : undefined
                                }
                                onRowHover={
                                    isSideBySide
                                        ? (item) =>
                                              setHoveredItemId(
                                                  item
                                                      ? getChartItemId(item)
                                                      : null,
                                              )
                                        : undefined
                                }
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px',
                        }}
                    >
                        {visualization.enabled && (
                            <ReportWidgetChart
                                items={chartItems}
                                chartType={visualization.type}
                                options={visualization.options}
                                loading={loading}
                                disableAnimation={disableChartAnimation}
                                onItemClick={
                                    onChartItemClick
                                        ? handleChartItemClick
                                        : undefined
                                }
                                activeItemId={hoveredItemId ?? undefined}
                                onItemHover={(item) =>
                                    setHoveredItemId(item?.id ?? null)
                                }
                            />
                        )}

                        {table.enabled && (
                            <ReportWidgetTable
                                items={visibleItems}
                                columns={table.columns}
                                loading={isInitialLoading}
                                loadingMore={isLoadingMore}
                                hasMore={hasMore}
                                onLoadMore={handleLoadMore}
                                itemColors={itemColors}
                                options={{
                                    ...table.options,
                                    loadMoreCount,
                                }}
                                onRowClick={
                                    onRowClick ? handleRowClick : undefined
                                }
                                onDetailsClick={
                                    onDetailsClick
                                        ? handleDetailsClick
                                        : undefined
                                }
                                onRowHover={
                                    isSideBySide
                                        ? (item) =>
                                              setHoveredItemId(
                                                  item
                                                      ? getChartItemId(item)
                                                      : null,
                                              )
                                        : undefined
                                }
                            />
                        )}
                    </Box>
                )}
            </Box>
        );
    },
);

ComposableReportWidget.displayName = 'ComposableReportWidget';
